// ─────────────────────────────────────────────────────────────────────────────
// Automation Insights — Assignment Group Scope Context (added 2026-09-11)
//
// New context "alongside the existing AutomationFilterProvider," per
// AUTOMATION_INSIGHTS_MIGRATION_CHECKLIST.md's Phase 12 requirement: whatever
// platform + assignment-group selection is made in Platform Summary (by
// expanding a platform row and picking one of its real assignment groups)
// must be honored by Platform Performance, which renders below it on the
// page. Time filters stay in AutomationFilterContext — this context only
// carries the platform/assignment-group "scope," which is a separate
// concern (and Platform Summary itself, unlike Platform Performance, has no
// time-independent reason to read this back — it's a one-way broadcast from
// Platform Summary down to Platform Performance).
//
// Scope note: single assignment_group selection only for this first cut, not
// the multi-select the checklist describes — the backend endpoint
// (get_panel_platform_summary's new assignment_group param) takes one group
// at a time. Multi-select would need a backend aggregation change (summing
// across a list of groups) on top of this; flagged as a disclosed follow-up,
// not silently dropped.
// ─────────────────────────────────────────────────────────────────────────────

import React, { createContext, useContext, useState } from 'react';

export interface AssignmentGroupScope {
  /** Platform query_key currently drilled into via Platform Summary's expand,
   * or null when no platform is drilled into (whole-platform-table view). */
  platform: string | null;
  /** Real assignment-group name selected within that platform, or null for
   * the whole platform (equivalent to the backend's '__ALL__' sentinel). */
  assignmentGroup: string | null;
}

export interface AssignmentGroupScopeContextValue {
  scope: AssignmentGroupScope;
  setScope: (scope: AssignmentGroupScope) => void;
  clearScope: () => void;
}

const DEFAULT_SCOPE: AssignmentGroupScope = { platform: null, assignmentGroup: null };

export const AssignmentGroupScopeContext = createContext<AssignmentGroupScopeContextValue>({
  scope: DEFAULT_SCOPE,
  setScope: () => {},
  clearScope: () => {},
});

export const useAssignmentGroupScope = (): AssignmentGroupScopeContextValue => useContext(AssignmentGroupScopeContext);

export function AssignmentGroupScopeProvider({ children }: { children: React.ReactNode }) {
  const [scope, setScope] = useState<AssignmentGroupScope>(DEFAULT_SCOPE);
  const clearScope = () => setScope(DEFAULT_SCOPE);

  return (
    <AssignmentGroupScopeContext.Provider value={{ scope, setScope, clearScope }}>
      {children}
    </AssignmentGroupScopeContext.Provider>
  );
}
